import { Link, useSearch } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/src/lib/components/ui/button";
import Icon from "../../lib/components/custom/Icon";
import { Badge } from "../../lib/components/ui/badge";
import { Card } from "../../lib/components/ui/card";
import { Separator } from "../../lib/components/ui/separator";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

const refundSearchSchema = z.object({
	orderId: z.coerce.number().optional(),
});

export default function Refund() {
	const { t } = useTranslation();
	const { orders } = useApi();
	const search = useSearch({ from: "/refund" });
	const parsedSearch = refundSearchSchema.safeParse(search);
	const orderId = parsedSearch.success ? parsedSearch.data.orderId : undefined;

	const {
		data: orderData,
		isLoading,
		error,
	} = orders.getById({ orderId: orderId?.toString() || "" });

	const getRefundStatusColor = (status?: string | null) => {
		switch (status) {
			case "succeeded":
				return "bg-chart-2/10 text-chart-2";
			case "pending":
				return "bg-chart-1/10 text-chart-1";
			case "failed":
				return "bg-chart-5/10 text-chart-5";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const getRefundStatusText = (status?: string | null) => {
		switch (status) {
			case "succeeded":
				return t("refund.status.completed");
			case "pending":
				return t("refund.status.processing");
			case "failed":
				return t("refund.status.failed");
			default:
				return t("refund.status.na");
		}
	};

	if (!orderId) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="container max-w-lg mx-auto"
					>
						<Card className="p-8">
							<div className="text-center py-8">
								<motion.div
									initial={{ scale: 0.9, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ delay: 0.1, duration: 0.2, ease: "easeOut" }}
								>
									<Icon
										name="CircleAlert"
										className="w-16 h-16 mx-auto mb-4 text-muted-foreground"
									/>
								</motion.div>
								<h2 className="text-2xl font-semibold mb-2">
									{t("refund.no_order_selected.header")}
								</h2>
								<p className="text-muted-foreground mb-6">
									{t("refund.no_order_selected.subheader")}
								</p>
								<Link to="/orders">
									<Button variant="primary" size="lg" asChild>
										<span>{t("refund.view_orders")}</span>
									</Button>
								</Link>
							</div>
						</Card>
					</motion.div>
				</div>
			</Layout>
		);
	}

	if (isLoading) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="container mx-auto px-4 max-w-4xl">
						<Card className="p-6">
							<Skeleton className="h-8 w-64 mb-4" />
							<Skeleton className="h-4 w-full mb-2" />
							<Skeleton className="h-4 w-3/4" />
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	if (error || !orderData?.order) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="container max-w-lg mx-auto"
					>
						<Card className="p-8">
							<div className="text-center py-8">
								<motion.div
									initial={{ scale: 0.9, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ delay: 0.1, duration: 0.2, ease: "easeOut" }}
								>
									<Icon
										name="CircleAlert"
										className="w-16 h-16 mx-auto mb-4 text-chart-5"
									/>
								</motion.div>
								<h2 className="text-2xl font-semibold mb-2">
									{t("refund.order_not_found.header")}
								</h2>
								<p className="text-muted-foreground mb-6">
									{t("refund.order_not_found.subheader", { orderId })}
								</p>
								<Link to="/orders">
									<Button variant="primary" size="lg" asChild>
										<span>{t("refund.view_orders")}</span>
									</Button>
								</Link>
							</div>
						</Card>
					</motion.div>
				</div>
			</Layout>
		);
	}

	const order = orderData.order;
	const hasRefund = order.refundId && order.refundStatus;

	if (!hasRefund) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="container max-w-lg mx-auto"
					>
						<Card className="p-8">
							<div className="text-center py-8">
								<motion.div
									initial={{ scale: 0.9, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ delay: 0.1, duration: 0.2, ease: "easeOut" }}
								>
									<Icon
										name="Info"
										className="w-16 h-16 mx-auto mb-4 text-muted-foreground"
									/>
								</motion.div>
								<h2 className="text-2xl font-semibold mb-2">
									{t("refund.no_refund_found.header")}
								</h2>
								<p className="text-muted-foreground mb-6">
									{t("refund.no_refund_found.subheader")}
								</p>
								<Link to="/orders">
									<Button variant="primary" size="lg" asChild>
										<span>{t("refund.view_orders")}</span>
									</Button>
								</Link>
							</div>
						</Card>
					</motion.div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-8 border rounded-large bg-gradient-main min-h-full">
				<div className="container mx-auto max-w-4xl">
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="mb-6"
					>
						<Link
							to="/orders"
							className="text-muted-foreground hover:text-foreground flex items-center gap-2 mb-4 transition-colors"
						>
							<Icon name="ArrowLeft" className="w-4 h-4" />
							{t("refund.header_back_to_orders")}
						</Link>
						<h1 className="text-3xl font-bold font-manrope">
							{t("refund.header_title")}
						</h1>
						<p className="text-muted-foreground mt-2">
							{t("refund.header_order_id", { id: order.id })}
						</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.05, ease: "easeOut" }}
					>
						<Card className="p-6 mb-6">
							<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
								<div>
									<h2 className="text-xl font-semibold mb-1">
										{t("refund.info.header")}
									</h2>
									<p className="text-sm text-muted-foreground">
										{t("refund.info.subheader")}
									</p>
								</div>
								<Badge
									className={`${getRefundStatusColor(order.refundStatus)} px-3 py-1 text-sm`}
								>
									{getRefundStatusText(order.refundStatus)}
								</Badge>
							</div>

							<Separator className="my-6" />

							<div className="space-y-4">
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">
										{t("refund.info.refund_id")}
									</span>
									<span className="font-mono text-sm bg-muted/50 px-2 py-1 rounded">
										{order.refundId}
									</span>
								</div>

								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">
										{t("refund.info.refund_amount")}
									</span>
									<span className="font-bold text-lg text-primary">
										€{Number(order.refundAmount || 0).toFixed(2)}
									</span>
								</div>

								{order.refundReason && (
									<div className="flex justify-between items-start">
										<span className="text-muted-foreground">
											{t("refund.info.reason")}
										</span>
										<span className="text-right max-w-xs">
											{order.refundReason}
										</span>
									</div>
								)}

								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">
										{t("refund.info.order_status")}
									</span>
									<Badge variant="outline">{order.status}</Badge>
								</div>

								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">
										{t("refund.info.order_date")}
									</span>
									<span>{new Date(order.orderDate).toLocaleDateString()}</span>
								</div>
							</div>
						</Card>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.1, ease: "easeOut" }}
					>
						<Card className="p-6">
							<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
								<Icon name="Clock" className="w-5 h-5 text-muted-foreground" />
								{t("refund.expect.header")}
							</h3>
							<div className="space-y-3 text-sm text-muted-foreground">
								{order.refundStatus === "pending" && (
									<>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-1 shrink-0" />
											{t("refund.expect.pending.line1")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-1 shrink-0" />
											{t("refund.expect.pending.line2")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-1 shrink-0" />
											{t("refund.expect.pending.line3")}
										</p>
									</>
								)}
								{order.refundStatus === "succeeded" && (
									<>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-2 shrink-0" />
											{t("refund.expect.succeeded.line1")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-2 shrink-0" />
											{t("refund.expect.succeeded.line2")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-2 shrink-0" />
											{t("refund.expect.succeeded.line3")}
										</p>
									</>
								)}
								{order.refundStatus === "failed" && (
									<>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-5 shrink-0" />
											{t("refund.expect.failed.line1")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-5 shrink-0" />
											{t("refund.expect.failed.line2")}
										</p>
										<p className="flex items-start gap-2">
											<span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-5 shrink-0" />
											{t("refund.expect.failed.line3")}
										</p>
									</>
								)}
							</div>
						</Card>
					</motion.div>
				</div>
			</div>
		</Layout>
	);
}
